import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { getStoreEmailSettings, saveStoreEmailSettings } from '../../../utils/queries';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';
import {
	FONT_FAMILY_OPTIONS,
	BUTTON_STYLE_OPTIONS,
	BORDER_RADIUS_OPTIONS,
	fontOption,
	buttonStyleOption,
	borderRadiusOption,
} from '../../../constants/branding';

// One row in the stacked section list. Collapsed, it's a single clickable
// summary line. Active, it splits into a label column (left, click again to
// close) and the picker grid (right) in the same row -- the editor opens
// beside the row, not below it. No box border per row; rows are separated by
// the parent list's divide-y instead.
function ThemeSectionRow({ title, preview, active, onToggle, children }) {
	return (
		<div className={`flex flex-col items-start sm:flex-row ${active ? 'py-4' : ''}`}>
			<button
				type='button'
				onClick={onToggle}
				className={`flex items-center gap-4 text-left px-4 shrink-0 w-full sm:w-96 ${active ? '' : 'py-3 hover:bg-gray-50'}`}
			>
				<div className='flex-1 min-w-0'>
					<div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>{title}</div>
					{preview}
				</div>
				{!active && <ChevronRight className='size-4 shrink-0 text-gray-400' />}
			</button>
			{active && (
				<div className='flex-1 min-w-0 px-4 pt-3 sm:pt-0 sm:pl-6 sm:border-l border-gray-200'>{children}</div>
			)}
		</div>
	);
}

function PickerGrid({ children }) {
	return <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>{children}</div>;
}

function PickerOption({ selected, onClick, disabled, children }) {
	return (
		<button
			type='button'
			onClick={disabled ? undefined : onClick}
			aria-disabled={disabled}
			className={`text-left border rounded-lg p-3 bg-white ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${
				selected ? 'border-black ring-1 ring-black' : 'border-gray-200 hover:border-gray-300'
			}`}
		>
			{children}
		</button>
	);
}

function FontPreview({ css }) {
	return (
		<div style={{ fontFamily: css }}>
			<div className='text-xl font-semibold leading-tight text-gray-900'>Heading</div>
			<div className='text-sm text-gray-500'>This is your paragraph.</div>
		</div>
	);
}

function ButtonPreview({ radius, label }) {
	return (
		<span
			style={{ borderRadius: radius }}
			className='inline-block bg-gray-900 text-white text-xs font-medium px-4 py-2'
		>
			{label || 'Button'}
		</span>
	);
}

function RadiusPreview({ radius }) {
	return <div style={{ borderRadius: radius }} className='size-10 border-2 border-gray-400 bg-transparent' />;
}

function ColorSwatchPair({ primary, secondary }) {
	return (
		<div className='flex items-center gap-2'>
			<span className='size-10 rounded-full border border-gray-200' style={{ background: primary || '#e5e7eb' }} />
			<span className='size-10 rounded-full border border-gray-200' style={{ background: secondary || '#e5e7eb' }} />
		</div>
	);
}

export default function StorefrontThemeTab({ storeId }) {
	const canEdit = useCan(PLATFORMADMIN_ROLES);
	const [settings, setSettings] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [activeSection, setActiveSection] = useState(null);
	const toggleSection = (id) => setActiveSection((current) => (current === id ? null : id));

	useEffect(() => {
		setLoading(true);
		getStoreEmailSettings(storeId).then((result) => {
			setSettings(result?.success ? result.data || {} : {});
			setLoading(false);
		});
	}, [storeId]);

	// Sequelize's upsertSingleton does a partial .update() -- sending just the
	// one changed field here won't touch logoUrl/supportEmail/etc, which live
	// on this same store_email_settings row via the Identity tab.
	const assign = async (fields) => {
		if (!canEdit) return;
		const previous = settings;
		setSettings((s) => ({ ...s, ...fields }));
		setSaving(true);
		setError('');
		const result = await saveStoreEmailSettings(storeId, fields);
		setSaving(false);
		if (!result?.success) {
			setSettings(previous);
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setSettings(result.data);
	};

	if (loading) {
		return <div className='text-sm text-gray-400 py-4'>Loading...</div>;
	}

	const selectedFont = fontOption(settings.storefrontFontFamily);
	const selectedButtonStyle = buttonStyleOption(settings.storefrontButtonStyle);
	const selectedBorderRadius = borderRadiusOption(settings.storefrontBorderRadius);

	return (
		<div className='max-w-6xl'>
			{error && <div className='text-sm text-pi-red mb-3'>{error}</div>}
			{!canEdit && <p className='text-xs text-gray-400 mb-3'>Only a Platform Administrator can change these settings.</p>}

			<div className='divide-y divide-gray-200'>
				<ThemeSectionRow
					title='Fonts'
					active={activeSection === 'font'}
					onToggle={() => toggleSection('font')}
					preview={
						<>
							<FontPreview css={selectedFont.css} />
							<div className='text-xs text-gray-400 mt-1'>{selectedFont.label}</div>
						</>
					}
				>
					<PickerGrid>
						{FONT_FAMILY_OPTIONS.map((option) => (
							<PickerOption
								key={option.value}
								selected={option.value === selectedFont.value}
								disabled={saving || !canEdit}
								onClick={() => assign({ storefrontFontFamily: option.value })}
							>
								<FontPreview css={option.css} />
								<div className='text-xs text-gray-400 mt-2'>{option.label}</div>
							</PickerOption>
						))}
					</PickerGrid>
				</ThemeSectionRow>

				<ThemeSectionRow
					title='Buttons'
					active={activeSection === 'buttons'}
					onToggle={() => toggleSection('buttons')}
					preview={<ButtonPreview radius={selectedButtonStyle.radius} />}
				>
					<PickerGrid>
						{BUTTON_STYLE_OPTIONS.map((option) => (
							<PickerOption
								key={option.value}
								selected={option.value === selectedButtonStyle.value}
								disabled={saving || !canEdit}
								onClick={() => assign({ storefrontButtonStyle: option.value })}
							>
								<div className='flex flex-col items-center gap-2 py-1'>
									<ButtonPreview radius={option.radius} />
									<span className='text-xs text-gray-500'>{option.label}</span>
								</div>
							</PickerOption>
						))}
					</PickerGrid>
				</ThemeSectionRow>

				<ThemeSectionRow
					title='Border Radius'
					active={activeSection === 'radius'}
					onToggle={() => toggleSection('radius')}
					preview={
						<div className='flex items-center gap-3'>
							<RadiusPreview radius={selectedBorderRadius.radius} />
							<span className='text-sm text-gray-500'>{selectedBorderRadius.label}</span>
						</div>
					}
				>
					<PickerGrid>
						{BORDER_RADIUS_OPTIONS.map((option) => (
							<PickerOption
								key={option.value}
								selected={option.value === selectedBorderRadius.value}
								disabled={saving || !canEdit}
								onClick={() => assign({ storefrontBorderRadius: option.value })}
							>
								<div className='flex flex-col items-center gap-2 py-1'>
									<RadiusPreview radius={option.radius} />
									<span className='text-xs text-gray-500'>{option.label}</span>
								</div>
							</PickerOption>
						))}
					</PickerGrid>
				</ThemeSectionRow>

				<ThemeSectionRow
					title='Colors'
					active={activeSection === 'colors'}
					onToggle={() => toggleSection('colors')}
					preview={
						<ColorSwatchPair primary={settings.storefrontPrimaryColor} secondary={settings.storefrontSecondaryColor} />
					}
				>
					<div className='space-y-4 max-w-sm'>
						{[
							{ key: 'storefrontPrimaryColor', label: 'Primary Color' },
							{ key: 'storefrontSecondaryColor', label: 'Secondary Color' },
						].map((f) => (
							<div key={f.key}>
								<label>{f.label}</label>
								<div className='flex items-center gap-2'>
									<input
										type='color'
										className='h-9 w-12'
										disabled={saving || !canEdit}
										value={settings[f.key] || '#000000'}
										onChange={(e) => assign({ [f.key]: e.target.value })}
									/>
									<input
										className='w-28 form-input'
										placeholder='#1a2b3c'
										disabled={saving || !canEdit}
										defaultValue={settings[f.key] || ''}
										key={settings[f.key]}
										onBlur={(e) =>
											e.target.value !== (settings[f.key] || '') && assign({ [f.key]: e.target.value })
										}
									/>
								</div>
							</div>
						))}
					</div>
				</ThemeSectionRow>
			</div>
		</div>
	);
}
